import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, Table, Form, Spinner } from 'react-bootstrap';
import { FaSortUp, FaSortDown, FaSort, FaChevronLeft, FaChevronRight, FaCaretRight, FaCaretDown, FaDesktop } from 'react-icons/fa';
import { fetchAuditlogs } from './store';
import Breadcrumbs from '../../breadcrumbs';
import img from '../../../images/no-data.svg';

// Breadcrumb data for navigation
const breadcrumbData = [
    { title: 'Audit Log' }
];

const Auditlog = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Data state
    const [auditData, setAuditData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Expanded rows state
    const [expandedRows, setExpandedRows] = useState({});

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Sort state
    const [sortConfig, setSortConfig] = useState({
        key: 'totalLogs',
        direction: 'desc'
    });



    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);



    // Fetch audit logs
    useEffect(() => {
        loadData();
    }, [currentPage, itemsPerPage, debouncedSearch, sortConfig]);

    const loadData = () => {
        setIsLoading(true);
        dispatch(fetchAuditlogs({
            page: currentPage,
            perPage: itemsPerPage,
            q: debouncedSearch,
            sort: sortConfig.direction,
            sortColumn: sortConfig.key
        }))
            .then((response) => {
                if (response.payload && response.payload.success) {
                    setAuditData(response.payload.data);
                    setTotalCount(response.payload.pagination.totalCount);
                    setTotalPages(response.payload.pagination.totalPages);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching audit logs:', error);
                setIsLoading(false);
            });
    };

    // Toggle row expansion
    const toggleRow = (userId) => {
        setExpandedRows(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };

    // Handle sort
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    // Get sort icon
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <FaSort className="sort-icon" />;
        }
        return sortConfig.direction === 'asc'
            ? <FaSortUp className="sort-icon active" />
            : <FaSortDown className="sort-icon active" />;
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Handle items per page change
    const handlePerPageChange = (value) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1);
    };



    return (
        <>
            <Breadcrumbs title={t("Audit Log")} data={breadcrumbData} />
            <div className="auditlog-wrapper">
                <Card className="auditlog-card">
                    <Card.Header className="auditlog-header">
                        <div className="header-left">
                            <h4 className="card-title">{t("User-wise Audit Table")}</h4>
                            <span className="total-count">
                                {totalCount} {t("users")}
                            </span>
                        </div>
                        <div className="header-right">
                            {/* Items per page */}
                            <Form.Select
                                className="per-page-select"
                                value={itemsPerPage}
                                onChange={(e) => handlePerPageChange(e.target.value)}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </Form.Select>
                        </div>
                    </Card.Header>

                    <Card.Body className="auditlog-body">
                        {/* Search Box */}
                        <div className="search-section">
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search by User ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        {isLoading ? (
                            <div className="loading-container">
                                <Spinner animation="border" variant="primary" />
                                <p>{t("Loading...")}</p>
                            </div>
                        ) : auditData.length === 0 ? (
                            <div className="empty-container">
                                <img src={img} alt="No data" className="empty-image" />
                                <p>{t("No audit logs found")}</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table className="auditlog-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}></th>
                                            <th onClick={() => handleSort('userId')} className="sortable">
                                                {t("UserID")} {getSortIcon('userId')}
                                            </th>
                                            <th onClick={() => handleSort('totalLogs')} className="sortable">
                                                {t("Total Logs")} {getSortIcon('totalLogs')}
                                            </th>
                                            <th onClick={() => handleSort('screensUsed')} className="sortable">
                                                {t("Screens Used")} {getSortIcon('screensUsed')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auditData.map((user, index) => (
                                            <React.Fragment key={user.userId || index}>
                                                {/* Parent Row */}
                                                <tr
                                                    className={`parent-row ${expandedRows[user.userId] ? 'expanded' : ''}`}
                                                    onClick={() => toggleRow(user.userId)}
                                                >
                                                    <td className="expand-cell">
                                                        {expandedRows[user.userId]
                                                            ? <FaCaretDown className="expand-icon" />
                                                            : <FaCaretRight className="expand-icon" />
                                                        }
                                                    </td>
                                                    <td className="user-cell">
                                                        <span className="user-badge">
                                                            {user.userId || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="logs-cell">
                                                        <span className="logs-badge">{user.totalLogs}</span>
                                                    </td>
                                                    <td className="screens-cell">
                                                        <span className="screens-badge">{user.screensUsed}</span>
                                                    </td>
                                                </tr>

                                                {/* Expanded Row - Screen Breakdown */}
                                                {expandedRows[user.userId] && (
                                                    <tr className="expanded-row">
                                                        <td colSpan="4" className="expanded-content">
                                                            <div className="screen-breakdown">
                                                                <div className="breakdown-header">
                                                                    <FaDesktop className="breakdown-icon" />
                                                                    <span>{t("Screen Name")}</span>
                                                                    <span className="breakdown-logs-header">{t("Total Logs")}</span>
                                                                </div>
                                                                <div className="breakdown-list">
                                                                    {user.screenBreakdown && user.screenBreakdown.map((screen, sIndex) => (
                                                                        <div key={sIndex} className="breakdown-item">
                                                                            <span className="screen-name-item">{screen.screenName || '-'}</span>
                                                                            <span className="screen-logs-item">{screen.totalLogs}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>

                    {/* Pagination */}
                    {!isLoading && auditData.length > 0 && (
                        <Card.Footer className="auditlog-footer">
                            <div className="pagination-info">
                                {t("Showing")} {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} {t("of")} {totalCount}
                            </div>
                            <div className="pagination-controls">
                                <button
                                    className="pagination-btn"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <FaChevronLeft />
                                </button>
                                <span className="page-info">
                                    {t("Page")} {currentPage} {t("of")} {totalPages}
                                </span>
                                <button
                                    className="pagination-btn"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    <FaChevronRight />
                                </button>
                            </div>
                        </Card.Footer>
                    )}
                </Card>
            </div>

            <style>{`
                .auditlog-wrapper {
                    padding: 20px 0;
                }

                .auditlog-card {
                    border-radius: 12px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                    border: none;
                    overflow: hidden;
                }

                .auditlog-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 15px;
                    padding: 20px 25px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }

                .header-left .card-title {
                    color: #fff;
                    margin: 0;
                    font-size: 1.5rem;
                    font-weight: 600;
                }

                .total-count {
                    background: rgba(255, 255, 255, 0.2);
                    padding: 5px 12px;
                    border-radius: 20px;
                    color: #fff;
                    font-size: 0.875rem;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                }

                .search-section {
                    padding: 20px;
                    border-bottom: 1px solid #e9ecef;
                }

                .search-input {
                    width: 100%;
                    padding: 12px 16px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    outline: none;
                    transition: all 0.2s;
                }

                .search-input:focus {
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .search-input::placeholder {
                    color: #a0aec0;
                }

                .per-page-select {
                    width: auto;
                    background: rgba(255, 255, 255, 0.9);
                    border: none;
                }

                .auditlog-body {
                    padding: 0;
                    min-height: 400px;
                }

                .loading-container,
                .empty-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    color: #666;
                }

                .empty-image {
                    width: 150px;
                    margin-bottom: 20px;
                    opacity: 0.6;
                }

                .auditlog-table {
                    margin: 0;
                }

                .auditlog-table thead th {
                    background: #f8f9fa;
                    border-bottom: 2px solid #e9ecef;
                    padding: 15px;
                    font-weight: 600;
                    color: #495057;
                    white-space: nowrap;
                }

                .auditlog-table thead th.sortable {
                    cursor: pointer;
                    user-select: none;
                    transition: background 0.2s;
                }

                .auditlog-table thead th.sortable:hover {
                    background: #e9ecef;
                }

                .sort-icon {
                    margin-left: 5px;
                    font-size: 0.75rem;
                    color: #adb5bd;
                }

                .sort-icon.active {
                    color: #667eea;
                }

                /* Parent Row Styles */
                .parent-row {
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .parent-row:hover {
                    background: #f8f9fa;
                }

                .parent-row.expanded {
                    background: #667eea10;
                }

                .parent-row td {
                    padding: 15px;
                    vertical-align: middle;
                    border-bottom: 1px solid #e9ecef;
                }

                .expand-cell {
                    width: 40px;
                    text-align: center;
                }

                .expand-icon {
                    color: #667eea;
                    font-size: 1rem;
                    transition: transform 0.2s;
                }

                .user-badge {
                    background: linear-gradient(135deg, #667eea20 0%, #764ba220 100%);
                    padding: 6px 14px;
                    border-radius: 8px;
                    font-size: 0.9rem;
                    color: #667eea;
                    font-weight: 600;
                }

                .logs-badge {
                    background: #e3f2fd;
                    color: #1976d2;
                    padding: 6px 14px;
                    border-radius: 8px;
                    font-weight: 600;
                }

                .screens-badge {
                    background: #e8f5e9;
                    color: #388e3c;
                    padding: 6px 14px;
                    border-radius: 8px;
                    font-weight: 600;
                }

                /* Expanded Row Styles */
                .expanded-row {
                    background: #fafafa;
                }

                .expanded-content {
                    padding: 0 !important;
                    border-bottom: 2px solid #667eea;
                }

                .screen-breakdown {
                    padding: 15px 20px 15px 60px;
                }

                .breakdown-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 15px;
                    background: #667eea15;
                    border-radius: 8px 8px 0 0;
                    font-weight: 600;
                    color: #495057;
                }

                .breakdown-icon {
                    color: #667eea;
                }

                .breakdown-logs-header {
                    margin-left: auto;
                }

                .breakdown-list {
                    background: #fff;
                    border: 1px solid #e9ecef;
                    border-top: none;
                    border-radius: 0 0 8px 8px;
                }

                .breakdown-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 12px 15px;
                    border-bottom: 1px solid #f1f3f5;
                    transition: background 0.2s;
                }

                .breakdown-item:last-child {
                    border-bottom: none;
                }

                .breakdown-item:hover {
                    background: #f8f9fa;
                }

                .screen-name-item {
                    color: #495057;
                    font-weight: 500;
                }

                .screen-logs-item {
                    color: #667eea;
                    font-weight: 600;
                }

                /* Footer & Pagination */
                .auditlog-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 25px;
                    background: #f8f9fa;
                }

                .pagination-info {
                    color: #666;
                    font-size: 0.875rem;
                }

                .pagination-controls {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .pagination-btn {
                    width: 36px;
                    height: 36px;
                    border: 1px solid #dee2e6;
                    background: #fff;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .pagination-btn:hover:not(:disabled) {
                    background: #667eea;
                    color: #fff;
                    border-color: #667eea;
                }

                .pagination-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .page-info {
                    color: #666;
                    font-size: 0.875rem;
                }

                @media (max-width: 768px) {
                    .auditlog-header {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .header-right {
                        width: 100%;
                    }

                    .search-box {
                        max-width: 100%;
                        flex: 1;
                    }

                    .auditlog-footer {
                        flex-direction: column;
                        gap: 10px;
                    }

                    .screen-breakdown {
                        padding-left: 20px;
                    }
                }
            `}</style>
        </>
    );
};

export default Auditlog;
